import { useEffect, useMemo, useRef } from "react";
import Skeleton from "react-loading-skeleton";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import "./upload-zip.input.scss";

export const UploadZipInput = ({
  value,
  onChange,
  loading,
  errors,
  existingFileName,
}) => {
  const inputRef = useRef();

  const handleClick = async () => {
    inputRef.current.click();
  };

  const handleFileChange = async ({ target }) => {
    const file = target.files[0];
    onChange?.(file);
  };

  const handleDelete = () => {
    try {
      onChange?.(undefined);
    } catch (error) {
      console.error("Error deleting file:", error);
      // Try with null as fallback
      try {
        onChange?.(null);
      } catch (fallbackError) {
        console.error("Error with fallback delete:", fallbackError);
      }
    }
  };

  const errorNotification = useMemo(() => {
    if (errors) {
      try {
        let content;
        if (Array.isArray(errors) && errors?.length > 0) {
          content = (
            <>
              <ul>
                {errors.map((error, index) => (
                  <li key={error?.code || index}>
                    {error?.message || String(error)}
                  </li>
                ))}
              </ul>
            </>
          );
        } else if (typeof errors === "object" && errors?.message) {
          content = <>{errors.message}</>;
        } else {
          content = <>{String(errors)}</>;
        }

        return (
          <Notification
            type={NotificationType.error}
            className="zip-file-input-errors"
          >
            {content}
          </Notification>
        );
      } catch (error) {
        console.error("Error rendering error notification:", error);
        return null;
      }
    }
    return null;
  }, [errors]);

  useEffect(() => {
    if (inputRef.current && errors) {
      inputRef.current.value = "";
    }
  }, [errors]);

  // Safely determine what to display
  const displayValue = value instanceof File ? value : existingFileName || null;
  const isExistingFile = !value && !!existingFileName;

  return (
    <div className="zip-file-input-container">
      {errorNotification}
      {displayValue ? (
        <div className="zip-file-input-preview">
          <div className="file-details">
            <div className="label">
              {isExistingFile ? "Current File" : "Selected File"}
            </div>
            <div className="file-name">
              <i className="fal fa-file-zipper" />
              <span>
                {value instanceof File
                  ? value?.name || "Unknown file"
                  : existingFileName || "Unknown file"}
              </span>
            </div>
          </div>
          <div className="separator"></div>
          <Button icon={"fal fa-trash-can"} onClick={handleDelete} />
        </div>
      ) : (
        <label htmlFor="zip-file-input" className="zip-file-input-label">
          {loading ? (
            <Skeleton rounded height="4rem" width="100%" />
          ) : (
            <Button
              className="upload-button"
              icon={"fal fa-upload"}
              onClick={handleClick}
            >
              Upload
            </Button>
          )}
          <input
            id="zip-file-input"
            type="file"
            ref={inputRef}
            onChange={handleFileChange}
            disabled={loading}
            accept="application/zip"
          />
        </label>
      )}
    </div>
  );
};
